/**
 * Bộ phận giao diện dùng chung — cố ý viết tay, không dùng thư viện UI nặng.
 * Quy ước màu: xanh dương = chủ đạo/hành động, xanh lá = ĐANG BẬT,
 * xám = TẮT/không hoạt động, đỏ = cảnh báo hoặc thao tác nguy hiểm.
 */

import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';
import { Tooltip } from '@fluentui/react-components';
import {
  CAU_HOI_BO_NHAP, laTrenCung, useChanKeoTaiLai, useGiuKhiLui, useHoiKhiTaiLai, vaoNgan,
} from '../lib/giunhap';

/* ------------------------------------------------------------------ nút */

type Variant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-nhan text-chu-1 hover:bg-nhan focus:ring-nhan/40 disabled:bg-nhan/40',
  secondary: 'bg-nen-1 text-chu-1 border border-vien-manh hover:bg-nen-2 focus:ring-vien disabled:text-chu-3',
  danger: 'bg-nguy text-chu-1 hover:bg-nguy focus:ring-nguy/40 disabled:bg-nguy/40',
  success: 'bg-tot text-chu-1 hover:bg-tot focus:ring-tot/40 disabled:bg-tot/40',
  ghost: 'bg-transparent text-chu-2 hover:bg-nen-2 focus:ring-vien disabled:text-chu-3',
};

const SIZES: Record<Size, string> = {
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-3.5 py-2 text-sm gap-2',
  lg: 'px-5 py-2.5 text-base gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
}

/**
 * GD-11 — NƠI DUY NHẤT được viết chữ nghiêng trong `web/src`.
 * Tệp 29 mục 2: nghiêng CHỈ cho trạng thái "chưa có số liệu / chưa rõ" (token `--chua-ro`).
 * Mọi chỗ cần nghiêng phải DÙNG hằng này, để mỗi chỗ nghiêng là một lời khai có tên, soát được
 * bằng một lần tìm. `npm run kiem:chu` báo đỏ mọi `italic` viết thẳng ở nơi khác — kể cả khi
 * nó mang màu xám (vòng 1 Codex: màu không phải giấy phép).
 */
export const LOP_CHUA_RO = 'italic text-chua-ro';

export function Button({
  variant = 'secondary', size = 'md', loading = false, icon, block = false,
  className = '', children, disabled, ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={[
        'inline-flex items-center justify-center rounded-lg font-medium transition',
        'focus:outline-none focus:ring-2 disabled:cursor-not-allowed',
        VARIANTS[variant], SIZES[size], block ? 'w-full' : '', className,
      ].join(' ')}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------- gợi ý ⓘ */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * GD-13 · GỢI Ý ⓘ — chỗ ở của mọi câu giải thích, thay cho câu văn dưới số (L-KHÔNG-DIỄN-GIẢI).
 *
 * Fluent `Tooltip`, và phiếu đòi ba đường mở, không chỉ di chuột:
 *   - Tab tới nút ⇒ Fluent tự hiện (focus) · Enter/Space ⇒ `onClick` giữ nó MỞ
 *   - chạm (di động, không có hover) ⇒ `onClick` mở · Esc / rời đi ⇒ Fluent đóng
 * ⚠ `onClick` MỞ chứ không bật/tắt: Tab đã mở sẵn, bật/tắt thì Enter lại đóng mất — đúng
 *   phím người dùng bàn phím bấm để "mở".
 * ⛔ Nút có `aria-label` nói RÕ nó giải thích cái gì; nội dung gắn qua `relationship="description"`
 *   (Fluent đặt `aria-describedby`) để trình đọc màn hình đọc được cả hai.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function GoiY({ noiDung, nhan }: {
  /** Câu giải thích — nguyên câu trước đây nằm dưới số. */
  noiDung: string;
  /** Tên thứ được giải thích, để đặt `aria-label` ("Giải thích: …"). */
  nhan: string;
}) {
  const [mo, setMo] = useState(false);
  return (
    <Tooltip
      content={noiDung}
      relationship="description"
      withArrow
      visible={mo}
      onVisibleChange={(_, d) => setMo(d.visible)}
    >
      <button
        type="button"
        aria-label={`Giải thích: ${nhan}`}
        onClick={() => setMo(true)}
        className="inline-grid h-5 w-5 shrink-0 place-items-center rounded-full align-middle text-chu-2
                   hover:text-chu-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-nhan/60"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

/* ----------------------------------------------------------------- thẻ */

export function Card({
  id, title, goiY, subtitle, actions, className = '', bodyClass = 'p-4', children,
}: {
  /** GD-13 — neo để liên kết "Xem tất cả ↓" nhảy tới đúng thẻ. */
  id?: string;
  title?: ReactNode;
  /**
   * GD-13 — ⓘ cạnh tiêu đề. ⛔ PHẢI đi qua prop này, KHÔNG nhét `<GoiY>` vào `title`: tiêu đề nằm
   * trong `<h2 class="truncate">` (`overflow: hidden`), và Fluent coi nút ⓘ trong đó là "bị cắt
   * khuất" nên đặt tooltip `visibility: hidden` — đo được 29/09: Tab+Enter và chạm đều không mở.
   */
  goiY?: { nhan: string; noiDung: string };
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
  bodyClass?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className={`card ${className}`}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-vien px-4 py-3">
          <div className="min-w-0">
            {title && (goiY ? (
              <div className="flex min-w-0 items-center gap-1.5">
                <h2 className="truncate text-sm font-semibold text-chu-1">{title}</h2>
                <GoiY nhan={goiY.nhan} noiDung={goiY.noiDung} />
              </div>
            ) : (
              <h2 className="truncate text-sm font-semibold text-chu-1">{title}</h2>
            ))}
            {subtitle && <p className="mt-0.5 truncate text-xs text-chu-2">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

/** Ô số liệu lớn ở màn Tổng quan */
/**
 * 🔴 SỐ ĐO — THÀNH PHẦN DUY NHẤT ĐƯỢC PHÉP GHÉP SỐ VỚI ĐƠN VỊ.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * Ba luật của quy hoạch diện mạo nằm gọn trong một chỗ, nên không nơi nào phải nhớ chúng:
 *
 *  1 · **Chữ số đều bề rộng** (`.so-do`). Số phải ĐỨNG YÊN khi cập nhật — số nhảy chỗ mỗi
 *      lần đổi là dấu hiệu rõ nhất của "trông như web", và nó làm mắt không so được hai
 *      dòng liền nhau.
 *
 *  2 · **Đơn vị NHỎ HƠN và NHẠT HƠN số.** `220.4 V`, không phải `220.4V`, cũng không phải
 *      hai chữ cùng cỡ. Người trực đọc **con số**; đơn vị chỉ để xác nhận.
 *
 *  3 · **"CHƯA RÕ" TRÔNG KHÁC "TỐT".** `giaTri == null` ⇒ xám, **chữ nghiêng**, và ⛔ KHÔNG
 *      hiện đơn vị — một đơn vị đứng cạnh dấu gạch làm người đọc tưởng có số liệu.
 *
 * ⚠ Mức 3 là mức hay bị nuốt nhất, và là mức đã đẻ ra `switchPos` · `gpsFix` · `rtcLostPower`.
 *   Không có số liệu là một TRẠNG THÁI, không phải một chỗ trống để lấp bằng số 0.
 *
 * ⛔ ĐỪNG tự ghép `{`${v} V`}` ở bất kỳ đâu: ghép tay thì mỗi chỗ ghép một kiểu, và không
 *   chỗ nào trong số đó vẽ được mức "chưa rõ".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function Metric({
  nhan, giaTri, donVi, manh = false, coNho = false,
}: {
  /** Nhãn phía trên. Bỏ trống thì chỉ hiện số — dùng khi đã có nhãn ở cột bảng. */
  nhan?: ReactNode;
  /** 🔴 `null`/`undefined` = CHƯA CÓ SỐ LIỆU. Không phải 0, và không được vẽ như 0. */
  giaTri: string | number | null | undefined;
  donVi?: string;
  /** Nổi bật: số to hơn, nền nhạt. Dùng cho một hai số chính, không dùng cho cả bảng. */
  manh?: boolean;
  /** Dùng trong ô bảng dày — bỏ khung, bỏ đệm. */
  coNho?: boolean;
}) {
  const chuaRo = giaTri === null || giaTri === undefined || giaTri === '';
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-03` 11/09/2026 — SỐ **TO** DÙNG CHỮ TIÊU ĐỀ (Playfair); số nhỏ giữ nguyên.
   *
   * 🔴 ĐÂY LÀ LẦN ĐẦU `ui.tsx` BỊ ĐỤNG, và phiếu giới hạn rất hẹp: **chỉ lớp chữ, không props**.
   *   Nên thay đổi duy nhất là chuỗi lớp của con số — mọi chỗ gọi `Metric` trong app không
   *   phải biết gì.
   *
   * ⛔ VÀ RANH GIỚI "TO / NHỎ" KHÔNG PHẢI CHUYỆN GU — `thietke.css` chốt nó: *chữ có chân ở cỡ
   *   nhỏ GIẢM TỐC ĐỘ ĐỌC trên màn hình, mà bảng số là thứ người trực quét bằng mắt.* Nên:
   *       `manh`  (text-xl, 20 px)  → `chu-tieude`  ← số chính, đọc từng cái một
   *       thường / `coNho` (bảng)   → `so-do` (mono) ← số quét hàng loạt, phải thẳng cột
   *   Đổi cả hai là làm chậm đúng chỗ cần nhanh.
   * ⚠ `chu-tieude` mang sẵn `lining-nums tabular-nums` — bắt buộc, vì Playfair mặc định vẽ số
   *   kiểu cũ (3, 4, 7, 9 thò xuống dưới dòng) và các cột số thôi thẳng hàng.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  const than = (
    <span className={manh ? 'text-xl font-semibold' : 'font-semibold'}>
      {chuaRo ? (
        // ⛔ Không hiện đơn vị ở đây: "— V" làm người đọc tưởng có số liệu.
        <span className={`font-normal ${LOP_CHUA_RO}`}>chưa có số liệu</span>
      ) : (
        <>
          <span className={`${manh ? 'chu-tieude' : 'so-do'} text-chu-1`}>{giaTri}</span>
          {donVi && <span className="ml-1 text-xs font-normal text-chu-2">{donVi}</span>}
        </>
      )}
    </span>
  );

  if (coNho) return than;

  return (
    <div className={`rounded-lg border p-2.5 ${manh ? 'border-nhan/50 bg-nhan/15' : 'border-vien'}`}>
      {nhan && <p className="text-xs uppercase tracking-wide text-chu-2">{nhan}</p>}
      <p className="mt-0.5">{than}</p>
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * Ô SỐ LỚN — biểu tượng trái, nhãn nhỏ, một con số to, một dòng phụ.
 *
 * ➕ `GD-06` 11/09/2026 — DỜI TỪ `Dashboard.tsx` LÊN ĐÂY, và lý do không phải gọn gàng:
 *   `GD-06` cần đúng cái ô ấy cho trang Tuyến. Chép sang một bản thứ hai là dựng **hai chỗ
 *   phải nhớ khớp nhau** — cùng bệnh đã cắt bốn lần trong dự án này (bảng vai gõ tay, `TEN_LENH`
 *   gõ song song, hai nguồn màu trạng thái, hai chỗ tô ảnh đại diện). Hai bản ô số sẽ lệch
 *   nhau ở đúng thứ khó thấy nhất: mức "chưa có số liệu".
 *
 * 🔴 VÀ ĐÓ LÀ ĐIỂM SỐNG CỦA Ô NÀY: `tri === null` = **CHƯA ĐO ĐƯỢC**, không phải `0`.
 *   `Metric` giữ luật ấy; ô này chỉ được phép chuyển `null` xuống nguyên vẹn.
 * ⚠ Kiểu `tri` cố ý HẸP (`string | number | null`), không `ReactNode`: để `ReactNode` thì chỗ
 *   gọi nhét được một `<span>` vào, và `Metric` mất khả năng phân biệt "chưa có số liệu" với
 *   "có một thứ gì đó". Kiểu hẹp hơn là một phép kiểm chạy lúc biên dịch.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function OSoLon({ icon, mau, nhan, tri, donVi, phu, goiY, nhanPhu, huyHieu }: {
  icon: ReactNode;
  mau: 'tot' | 'nguy' | 'canh' | 'nhan' | 'tim';
  nhan: string;
  tri: string | number | null;
  donVi?: string;
  /**
   * Dòng chữ nhỏ dưới số. ⚠ GD-13 (L-KHÔNG-DIỄN-GIẢI): Tổng quan KHÔNG dùng nữa — giữ lại chỉ
   * vì trang Tuyến / Chiến lược còn gọi; hai trang ấy chưa nằm trong phạm vi GD-13.
   */
  phu?: ReactNode;
  /** GD-13 — câu giải thích đi vào ⓘ cạnh nhãn, không nằm dưới số. */
  goiY?: string;
  /** GD-13 — nhãn phụ NGẮN là dữ liệu (vd "1 / 8 tủ"), đứng cùng hàng nhãn, không phải câu. */
  nhanPhu?: ReactNode;
  /** GD-13 — huy hiệu dữ liệu dưới số (vd mức cảnh báo cao nhất đang mở). */
  huyHieu?: ReactNode;
}) {
  const NEN: Record<string, string> = {
    tot: 'bg-tot/15 text-tot',
    nguy: 'bg-nguy-nen text-nguy',
    canh: 'bg-canh-nen text-canh',
    nhan: 'bg-nhan/15 text-nhan',
    tim: 'bg-tim/15 text-tim',
  };
  return (
    <div className="card flex items-center gap-3 p-4">
      <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-nut ${NEN[mau]}`}>
        {icon}
      </span>
      <div className="min-w-0">
        {/*
          * ⚠ Nhãn KHÔNG `truncate`: thêm ⓘ (20 px) vào ô ¼ trang làm "Công suất hiện tại" bị cắt
          *   "CÔNG SUẤT HIỆ…" ở 1440 px (đo 29/09). Nhãn được XUỐNG DÒNG, không bị cắt, không hạ cỡ.
          */}
        <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-chu-2">
          <span className="min-w-0" data-nhan-o-so>{nhan}</span>
          {goiY && <GoiY noiDung={goiY} nhan={nhan} />}
        </p>
        {/*
          * ⚠ `nhanPhu` đứng CÙNG HÀNG SỐ, không cùng hàng nhãn: bản đầu đặt ở hàng nhãn và ảnh
          *   1440 px (29/09) cho thấy nhãn "Công suất hiện tại" bị cắt thành "CÔNG SU…" — cắt
          *   nhãn để chèn thứ khác là đúng điều tệp 29 mục 5.5 cấm.
          */}
        {nhanPhu ? (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <Metric giaTri={tri} donVi={donVi} manh coNho />
            <span className="whitespace-nowrap text-xs text-chu-2">{nhanPhu}</span>
          </div>
        ) : (
          <Metric giaTri={tri} donVi={donVi} manh coNho />
        )}
        {huyHieu && <div className="mt-1">{huyHieu}</div>}
        {phu && <p className="mt-0.5 truncate text-xs text-chu-3">{phu}</p>}
      </div>
    </div>
  );
}

export function StatTile({
  label, value, unit, icon, tone = 'slate', hint,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  icon?: ReactNode;
  tone?: 'slate' | 'brand' | 'green' | 'red' | 'amber';
  hint?: string;
}) {
  const tones: Record<string, string> = {
    slate: 'text-chu-1 bg-nen-2',
    brand: 'text-nhan bg-nhan/15',
    green: 'text-tot bg-tot/15',
    red: 'text-nguy bg-nguy-nen',
    amber: 'text-canh bg-canh-nen',
  };
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-chu-2">{label}</p>
          {/* Cùng ba luật với `Metric`: chữ số đều bề rộng, đơn vị nhỏ+nhạt, và "chưa rõ"
              trông khác "tốt". `StatTile` là ô to ở trang Tổng quan nên giữ riêng bố cục,
              nhưng KHÔNG được giữ riêng cách vẽ số. */}
          {/*
            * ➕ `GD-03` — `chu-tieude` thay `so-do`: đây là ô SỐ TO (24 px), đúng dải mà
            *   `thietke.css` giao cho chữ có chân. Cùng một quyết định với `Metric manh` ngay
            *   trên; hai chỗ đổi theo cùng một luật, không phải hai lần chọn riêng.
            * ⛔ "chưa có số liệu" GIỮ NGUYÊN chữ thường nghiêng — nó là một CÂU, không phải một
            *   con số. Cho nó vào chữ tiêu đề là vẽ một lời thú nhận trông như một phép đo.
            */}
          <p className="mt-1 text-2xl font-semibold text-chu-1">
            {value === null || value === undefined || value === ''
              ? <span className={`text-lg font-normal ${LOP_CHUA_RO}`}>chưa có số liệu</span>
              : <>
                  <span className="chu-tieude">{value}</span>
                  {unit && <span className="ml-1 text-sm font-normal text-chu-2">{unit}</span>}
                </>}
          </p>
          {hint && <p className="mt-1 text-xs text-chu-3">{hint}</p>}
        </div>
        {icon && <span className={`rounded-lg p-2 ${tones[tone]}`}>{icon}</span>}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- nhãn */

type BadgeTone = 'slate' | 'green' | 'red' | 'amber' | 'blue';

const BADGE_TONES: Record<BadgeTone, string> = {
  slate: 'bg-nen-2 text-chu-2 ring-vien',
  green: 'bg-tot/15 text-tot ring-tot/40',
  red: 'bg-nguy-nen text-nguy ring-nguy/40',
  amber: 'bg-canh-nen text-canh ring-canh/40',
  blue: 'bg-nhan/15 text-nhan ring-nhan/40',
};

export function Badge({
  tone = 'slate', children, className = '',
}: { tone?: BadgeTone; children: ReactNode; className?: string }) {
  return (
    /*
     * TK-29: `rounded-xl` thay `rounded-full`. Một dòng (cao ~20 px) thì góc 12 px tự kẹp về 10 px ⇒ y hệt viên thuốc cũ.
     *   Chỗ quá chật buộc chữ xuống dòng (thẻ ngõ ra hẹp: "Đang giữ bởi: CÔNG TẮC") thì thành chữ nhật bo góc, chữ nằm gọn
     *   trong viền — viên thuốc hai dòng thì chữ dạt vào chỗ cong, trông như tràn.
     */
    <span data-huy-hieu className={`inline-flex max-w-full items-center gap-1 rounded-xl [overflow-wrap:anywhere] px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${BADGE_TONES[tone]} ${className}`}>
      {children}
    </span>
  );
}

/** Chấm tròn trạng thái: xanh lá = online, xám = offline, đỏ = sự cố. */
export function StatusDot({
  online, size = 'md', pulse = false,
}: { online: boolean; size?: 'sm' | 'md'; pulse?: boolean }) {
  const s = size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5';
  return (
    <span className="relative inline-flex" title={online ? 'Đang kết nối' : 'Mất kết nối'}>
      <span className={`${s} rounded-full ${online ? 'bg-tot' : 'bg-chua-ro'}`} />
      {online && pulse && (
        <span className={`absolute inset-0 ${s} animate-ping rounded-full bg-tot opacity-60`} />
      )}
    </span>
  );
}

/* ------------------------------------------------------- ô nhập & nhãn */

export function Field({
  label, hint, error, required, children, className = '',
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      {label && (
        <span className="lbl">
          {label}
          {required && <span className="ml-0.5 text-nguy">*</span>}
        </span>
      )}
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-chu-3">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-nguy">{error}</span>}
    </label>
  );
}

/** Nút gạt bật/tắt — dùng cho ngõ ra và các cờ cấu hình. */
export function Switch({
  checked, onChange, disabled, labelOn = 'BẬT', labelOff = 'TẮT', busy = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  labelOn?: string;
  labelOff?: string;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-2',
        checked ? 'bg-tot focus:ring-tot/40' : 'bg-chua-ro focus:ring-vien',
        disabled || busy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ].join(' ')}
      title={checked ? labelOn : labelOff}
    >
      <span
        className={`absolute flex h-6 w-6 items-center justify-center rounded-full bg-nen-1 shadow transition-all ${
          checked ? 'left-7' : 'left-1'
        }`}
      >
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-chu-2" />}
      </span>
      <span className="sr-only">{checked ? labelOn : labelOff}</span>
    </button>
  );
}

/* -------------------------------------------------------------- hộp thoại */

/**
 * TK-27 — HỘP THOẠI CHUNG, và luật giữ dữ liệu đang nhập (xem `lib/giunhap.ts`):
 *   · chạm nền mờ KHÔNG đóng hộp (không có `onClick` trên nền — và phải giữ như thế);
 *   · khi đã gõ/chọn gì đó trong hộp, ✕ · Esc · "Quay lại" của máy KHÔNG đóng ngay mà hỏi lại;
 *   · nạp lại trang khi còn chữ chưa lưu ⇒ trình duyệt hỏi; hộp mở thì không "kéo để tải lại".
 * `hoiKhiDong={false}` chỉ cho hộp CHỈ ĐỂ XEM (không có gì để mất) — bộ kiểm giữ danh sách ấy.
 */
export function Modal({
  open, onClose, title, subtitle, children, footer, wide = false, danger = false, hoiKhiDong = true,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  danger?: boolean;
  /** Đã nhập mà đóng bằng ✕ / Esc / "Quay lại" thì hỏi lại. Mặc định CÓ. */
  hoiKhiDong?: boolean;
}) {
  const id = useId();
  /* Đã có ô nào trong hộp bị gõ / chọn / đánh dấu chưa — đọc từ chính sự kiện, không bắt nơi gọi khai. */
  const [daNhap, setDaNhap] = useState(false);
  const [dangHoi, setDangHoi] = useState(false);
  useEffect(() => { if (!open) { setDaNhap(false); setDangHoi(false); } }, [open]);

  const canGiu = open && hoiKhiDong && daNhap;
  const xinDong = () => { if (canGiu) setDangHoi(true); else onClose(); };
  const xinDongRef = useRef(xinDong);
  xinDongRef.current = xinDong;

  useChanKeoTaiLai(open);
  useHoiKhiTaiLai(canGiu);
  useGiuKhiLui(canGiu, () => setDangHoi(true));

  useEffect(() => {
    if (!open) return;
    const raNgan = vaoNgan(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !laTrenCung(id)) return;
      xinDongRef.current();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      raNgan();
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, id]);

  if (!open) return null;

  const daGo = () => { if (!daNhap) setDaNhap(true); };

  return (
    /*
     * ⛔ KHÔNG gắn `onClick` lên lớp nền này — chạm nhầm chỗ trống không được đóng hộp (TK-27).
     * 🔴 `margin: 0` VIẾT THẲNG, và đây là GỐC THẬT của lỗi lee gặp ở "Thêm tuyến chiếu sáng":
     *   hộp được vẽ NGAY TRONG trang, mà trang bọc bằng `space-y-4` ⇒ Tailwind đẩy mọi phần tử con
     *   (trừ con đầu) xuống `margin-top: 16px`. Lớp nền `fixed inset-0` bị đẩy theo ⇒ **dải 16 px trên
     *   cùng của thanh đầu trang KHÔNG bị che** — trông như chỗ trống mờ, nhưng chạm vào là trúng nút
     *   menu / ô tìm / chuông ở dưới ⇒ rời trang, hộp và chữ đang gõ mất theo.
     *   Lớp `m-0` không thắng được bộ chọn của `space-y-*` (nặng hơn); kiểu viết thẳng thì thắng.
     */
    <div className="fixed inset-0 z-50 flex items-end justify-center overscroll-none bg-nen-0/80 p-0 sm:items-center sm:p-4"
         style={{ margin: 0 }}
         data-nen-hop>
      <div
        className={`flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-nen-1 shadow-xl sm:rounded-2xl ${wide ? 'sm:max-w-4xl' : 'sm:max-w-lg'}`}
        role="dialog"
        aria-modal="true"
        onChangeCapture={daGo}
        onInputCapture={daGo}
      >
        <header className={`flex items-start justify-between gap-3 border-b px-4 py-3 ${danger ? 'border-nguy/50 bg-nguy-nen' : 'border-vien'}`}>
          <div className="min-w-0">
            <h3 className={`text-base font-semibold ${danger ? 'text-nguy' : 'text-chu-1'}`}>{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-chu-2">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={xinDong}
            className="rounded-lg p-1 text-chu-3 hover:bg-nen-2 hover:text-chu-1"
            aria-label="Đóng"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="thin-scroll flex-1 overflow-y-auto overscroll-contain px-4 py-4">{children}</div>

        {dangHoi && (
          <div role="alertdialog" aria-label={CAU_HOI_BO_NHAP.dau} data-hoi-bo-nhap
               className="flex flex-wrap items-center gap-2 border-t border-canh/50 bg-canh-nen px-4 py-3">
            <p className="min-w-0 flex-1 basis-56 text-sm text-chu-1">
              <b>{CAU_HOI_BO_NHAP.dau}</b> {CAU_HOI_BO_NHAP.than}
            </p>
            <Button variant="primary" onClick={() => setDangHoi(false)}>{CAU_HOI_BO_NHAP.giu}</Button>
            <Button variant="ghost" onClick={() => { setDangHoi(false); onClose(); }}>{CAU_HOI_BO_NHAP.bo}</Button>
          </div>
        )}

        {footer && !dangHoi && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-vien px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/** Hộp xác nhận đơn giản cho thao tác xoá / nguy hiểm. */
export function ConfirmDialog({
  open, title, message, confirmText = 'Xác nhận', cancelText = 'Huỷ', danger = true,
  loading = false, onConfirm, onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      danger={danger}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>{cancelText}</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>{confirmText}</Button>
        </>
      }
    >
      <div className="text-sm text-chu-1">{message}</div>
    </Modal>
  );
}

/* -------------------------------------------------------- trạng thái rỗng */

export function Spinner({ label = 'Đang tải…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-chu-2">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

export function EmptyState({ icon, title, hint, action }: {
  icon?: ReactNode; title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      {icon && <div className="text-chu-3">{icon}</div>}
      <p className="text-sm font-medium text-chu-2">{title}</p>
      {hint && <p className="max-w-md text-xs text-chu-3">{hint}</p>}
      {action}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Lỗi không xác định';
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-nguy/50 bg-nguy-nen p-4 text-sm text-nguy">
      <div className="flex items-center gap-2 font-medium">
        <XCircle className="h-4 w-4" /> Không tải được dữ liệu
      </div>
      <p className="text-xs text-nguy">{msg}</p>
      {onRetry && <Button size="sm" variant="secondary" onClick={onRetry}>Thử lại</Button>}
    </div>
  );
}

/** Băng cảnh báo vàng — dùng cho khối mất mát khi hạ giao thức v1. */
export function WarningBanner({
  title, items, children, tone = 'amber',
}: {
  title: ReactNode;
  items?: string[];
  children?: ReactNode;
  tone?: 'amber' | 'red' | 'blue';
}) {
  const tones = {
    amber: 'border-canh/50 bg-canh-nen text-canh',
    red: 'border-nguy/50 bg-nguy-nen text-nguy',
    blue: 'border-nhan/50 bg-nhan/15 text-nhan',
  } as const;
  const Icon = tone === 'blue' ? Info : AlertTriangle;
  return (
    <div className={`rounded-lg border p-3 text-sm ${tones[tone]}`}>
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{title}</p>
          {items && items.length > 0 && (
            <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs leading-relaxed">
              {items.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ thông báo */

type ToastKind = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  detail?: string;
}

interface ToastApi {
  show: (kind: ToastKind, message: string, detail?: string) => void;
  success: (message: string, detail?: string) => void;
  error: (message: string, detail?: string) => void;
  info: (message: string, detail?: string) => void;
  warning: (message: string, detail?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(1);

  const remove = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((kind: ToastKind, message: string, detail?: string) => {
    const id = seq.current++;
    setItems((list) => [...list.slice(-3), { id, kind, message, detail }]);
    window.setTimeout(() => remove(id), kind === 'error' ? 8000 : 4000);
  }, [remove]);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (m, d) => show('success', m, d),
    error: (m, d) => show('error', m, d),
    info: (m, d) => show('info', m, d),
    warning: (m, d) => show('warning', m, d),
  }), [show]);

  const style: Record<ToastKind, { cls: string; icon: ReactNode }> = {
    success: { cls: 'border-tot/50 bg-nen-1 text-tot', icon: <CheckCircle2 className="h-4 w-4 text-tot" /> },
    error: { cls: 'border-nguy/50 bg-nen-1 text-nguy', icon: <XCircle className="h-4 w-4 text-nguy" /> },
    info: { cls: 'border-nhan/50 bg-nen-1 text-nhan', icon: <Info className="h-4 w-4 text-nhan" /> },
    warning: { cls: 'border-canh/50 bg-nen-1 text-canh', icon: <AlertTriangle className="h-4 w-4 text-canh" /> },
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-2 z-[60] flex flex-col items-center gap-2 px-3 sm:left-auto sm:right-4 sm:items-end">
        {items.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto w-full max-w-sm animate-slide-in rounded-lg border px-3 py-2 shadow-lg ${style[t.kind].cls}`}
          >
            <div className="flex items-start gap-2">
              {style[t.kind].icon}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{t.message}</p>
                {t.detail && <p className="mt-0.5 break-words text-xs opacity-80">{t.detail}</p>}
              </div>
              <button
                type="button"
                onClick={() => remove(t.id)}
                className="rounded p-0.5 text-chu-3 hover:bg-nen-2"
                aria-label="Đóng thông báo"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast phải nằm trong <ToastProvider>');
  return ctx;
}

/* ------------------------------------------------------------ khác */

export function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h1 className="text-lg font-semibold text-chu-1 sm:text-xl">{children}</h1>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

/** Ô nhập giờ HH:MM có kiểm tra tại chỗ, trả về số phút từ 00:00. */
export function TimeInput({
  value, onChange, disabled, className = '',
}: {
  value: number;
  onChange: (min: number) => void;
  disabled?: boolean;
  className?: string;
}) {
  const toStr = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  return (
    <input
      type="time"
      className={`inp ${className}`}
      value={toStr(value)}
      disabled={disabled}
      step={60}
      onChange={(e) => {
        const [h, m] = e.target.value.split(':').map(Number);
        if (Number.isFinite(h) && Number.isFinite(m)) onChange((h * 60 + m) % 1440);
      }}
    />
  );
}
